import type { ErrorsObject } from 'types/server-error'
import type {
  SplitFieldError,
  SplitValidationError,
  ParsedSplitValidationErrors,
  TrackLevelValidationError,
} from 'types/rights-data'

import { parseServerError } from './captureError'

/**
 * Error message mapping for split validation errors.
 * Converts backend error messages to user-friendly messages.
 * Backend uses basis points (1 = 0.01%), so we convert to percentage-based language.
 */
const splitErrorMessageMapping: Record<string, string> = {
  'Ensure this value is greater than or equal to 1.':
    'Split must be at least 0.01%',
  'Ensure this value is less than or equal to 10000.':
    'Split cannot exceed 100%',
  'This field is required.': 'This field is required',
  'This field may not be null.': 'This field is required',
  'A valid integer is required.': 'Please enter a valid number',
}

/**
 * Maps field names from backend format to user-friendly display names.
 */
const fieldNameMapping: Record<string, string> = {
  split_percentage: 'Split',
  writer: 'Writer',
  publisher: 'Publisher',
  pro: 'PRO',
}

/**
 * Transforms an error message using the mapping, or returns the original if no mapping exists.
 */
const mapSplitErrorMessage = (message: string): string => {
  return splitErrorMessageMapping[message] || message
}

/**
 * Gets the user-friendly field name.
 */
export const getFieldDisplayName = (field: string): string => {
  return fieldNameMapping[field] || field
}

/**
 * Type guard to check if the error response contains split validation errors.
 * The backend returns errors in the format: { splits: [{}, { field: ["error"] }, ...] }
 */
export const hasSplitValidationErrors = (
  response: unknown,
): response is { splits: Array<Record<string, string[]>> } => {
  if (!response || typeof response !== 'object') return false
  const obj = response as Record<string, unknown>
  if (!Array.isArray(obj.splits)) return false
  return obj.splits.every(
    item => item && typeof item === 'object' && !Array.isArray(item),
  )
}

/**
 * Parses split validation errors from a backend response.
 *
 * The backend returns validation errors in the format:
 * ```json
 * {
 *   "splits": [
 *     {},
 *     { "split_percentage": ["Ensure this value is greater than or equal to 1."] },
 *     { "writer": ["This field is required."] },
 *     {}
 *   ]
 * }
 * ```
 *
 * This function extracts field-level errors for each split, mapping them to
 * user-friendly messages and associating them with the correct split ID (not index).
 *
 * @param response - The error response body from the API
 * @param splits - The current splits array to map backend indices to split IDs
 * @returns Parsed split validation errors with user-friendly messages
 *
 * @example
 * ```typescript
 * const response = {
 *   splits: [
 *     {},
 *     { split_percentage: ["Ensure this value is greater than or equal to 1."] }
 *   ]
 * }
 * const currentSplits = [
 *   { id: 1, writerId: 100, split: 50 },
 *   { id: 2, writerId: 101, split: 30 }
 * ]
 * const errors = parseSplitValidationErrors(response, currentSplits)
 * // Returns:
 * // {
 * //   splitErrors: [{
 * //     splitId: 2,  // Maps backend index 1 to split ID 2
 * //     fieldErrors: [{
 * //       field: 'split_percentage',
 * //       messages: ['Split must be at least 0.01%']
 * //     }]
 * //   }]
 * // }
 * ```
 */
export const parseSplitValidationErrors = (
  response: unknown,
  splits: Array<{ id: number | string }>,
): ParsedSplitValidationErrors => {
  const result: ParsedSplitValidationErrors = {
    splitErrors: [],
  }

  // Check for track-level validation errors first
  if (hasTrackLevelValidationErrors(response)) {
    result.trackErrors = parseTrackLevelValidationErrors(response)
    return result
  }

  // If it's not a valid split validation error format, try to extract a global error
  if (!hasSplitValidationErrors(response)) {
    if (response && typeof response === 'object') {
      const globalError = parseServerError(response as ErrorsObject)
      if (globalError && globalError !== 'Sorry, an error occurred') {
        result.globalError = globalError
      }
    }
    return result
  }

  const { splits: backendSplits } = response

  // Process each split in the array
  backendSplits.forEach((splitError, index) => {
    const fieldErrors: SplitFieldError[] = []

    // Extract field-level errors from this split
    Object.entries(splitError).forEach(([field, messages]) => {
      if (Array.isArray(messages) && messages.length > 0) {
        fieldErrors.push({
          field,
          messages: messages.map(mapSplitErrorMessage),
        })
      }
    })

    // Only add to result if this split has errors
    if (fieldErrors.length > 0) {
      // Map backend index to split ID
      const split = splits[index]
      if (split) {
        result.splitErrors.push({
          splitId: split.id,
          fieldErrors,
        })
      }
    }
  })

  return result
}

/**
 * Gets the first error message for a specific field from split validation errors.
 *
 * @param errors - Array of split validation errors
 * @param splitId - The ID of the split to check
 * @param field - The field name to get the error for
 * @returns The first error message for the field, or undefined if no error
 *
 * @example
 * ```typescript
 * const error = getSplitFieldError(errors, 2, 'split_percentage')
 * // Returns: 'Split must be at least 0.01%' or undefined
 * ```
 */
export const getSplitFieldError = (
  errors: SplitValidationError[],
  splitId: number | string,
  field: string,
): string | undefined => {
  const splitError = errors.find(e => e.splitId === splitId)
  if (!splitError) return undefined

  const fieldError = splitError.fieldErrors.find(fe => fe.field === field)
  return fieldError?.messages[0]
}

/**
 * Type guard to check if the error response contains track-level validation errors.
 * Track-level errors apply to the entire track rather than specific fields,
 * such as duplicate writer-publisher combinations.
 *
 * The backend returns track-level errors in the format:
 * ```json
 * {
 *   "splits": [
 *     {
 *       "track_id": "78500",
 *       "message": "Cannot have multiple splits with the same writer and publisher combination"
 *     }
 *   ]
 * }
 * ```
 *
 * This format differs from field-level errors which have field names as keys.
 *
 * @param response - The error response to check
 * @returns True if the response contains track-level validation errors
 */
export const hasTrackLevelValidationErrors = (
  response: unknown,
): response is {
  splits: Array<{ track_id: string; message: string }>
} => {
  if (!response || typeof response !== 'object') return false
  const obj = response as Record<string, unknown>
  if (!Array.isArray(obj.splits)) return false
  if (obj.splits.length === 0) return false

  // Check if the first item has track_id and message properties
  // This distinguishes track-level errors from field-level errors
  const firstItem = obj.splits[0]
  if (!firstItem || typeof firstItem !== 'object') return false

  return (
    'track_id' in firstItem &&
    'message' in firstItem &&
    typeof (firstItem as { track_id?: unknown }).track_id === 'string' &&
    typeof (firstItem as { message?: unknown }).message === 'string'
  )
}

/**
 * Parses track-level validation errors from a backend response.
 *
 * Track-level errors apply to the entire track rather than specific fields,
 * such as duplicate writer-publisher combinations.
 *
 * The backend returns track-level errors in the format:
 * ```json
 * {
 *   "splits": [
 *     {
 *       "track_id": "78500",
 *       "message": "Cannot have multiple splits with the same writer and publisher combination"
 *     }
 *   ]
 * }
 * ```
 *
 * @param response - The error response body from the API
 * @returns Array of track-level validation errors
 *
 * @example
 * ```typescript
 * const response = {
 *   splits: [{
 *     track_id: "78500",
 *     message: "Cannot have multiple splits with the same writer and publisher combination"
 *   }]
 * }
 * const errors = parseTrackLevelValidationErrors(response)
 * // Returns: [{
 * //   trackId: "78500",
 * //   message: "Cannot have multiple splits with the same writer and publisher combination"
 * // }]
 * ```
 */
export const parseTrackLevelValidationErrors = (
  response: unknown,
): TrackLevelValidationError[] => {
  if (!hasTrackLevelValidationErrors(response)) {
    return []
  }

  return response.splits.map(error => ({
    trackId: error.track_id,
    message: error.message,
  }))
}
